import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Building2,
  CheckCircle2,
  Home,
  Image,
  LogOut,
  MapPin,
  Sparkles,
  X,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { firestore, storage } from '@config/firebase';
import type { PropertyStatus, PropertyType, UtilitiesType } from '../../types/property.types';
import { getProvinces, getMetrosForProvince, getNeighborhoodsForMetro, type Province } from '@utils/locations';
import { getErrorMessage } from '@utils/errors';

type StatusOption = PropertyStatus;

interface FormState {
  title: string;
  description: string;
  propertyType: PropertyType;
  street: string;
  suburb: string;
  city: string;
  province: string;
  provinceCode: string;
  metro: string;
  neighborhoods: string[];
  postalCode: string;
  bedrooms: number;
  bathrooms: number;
  squareMeters: number;
  furnished: boolean;
  monthlyRent: number;
  deposit: number;
  utilities: UtilitiesType;
  amenities: string[];
  petFriendly: boolean;
  smokingAllowed: boolean;
  availableFrom: string;
  minLeaseDuration: number;
  maxOccupants: number;
  photoUrls: string[];
  primaryPhotoIndex: number;
  status: StatusOption;
}

const defaultFormState: FormState = {
  title: '',
  description: '',
  propertyType: 'apartment',
  street: '',
  suburb: '',
  city: '',
  province: '',
  provinceCode: '',
  metro: '',
  neighborhoods: [],
  postalCode: '',
  bedrooms: 0,
  bathrooms: 0,
  squareMeters: 0,
  furnished: false,
  monthlyRent: 0,
  deposit: 0,
  utilities: 'included',
  amenities: [],
  petFriendly: false,
  smokingAllowed: false,
  availableFrom: new Date().toISOString().split('T')[0],
  minLeaseDuration: 1,
  maxOccupants: 1,
  photoUrls: [],
  primaryPhotoIndex: 0,
  status: 'draft',
};

const CreateProperty: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userRole, logout } = useAuth();
  const [form, setForm] = useState<FormState>(defaultFormState);
  const [amenityInput, setAmenityInput] = useState('');
  const [photoInput, setPhotoInput] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [availableMetros, setAvailableMetros] = useState<string[]>([]);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState<string[]>([]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Load provinces on mount
  useEffect(() => {
    async function loadProvinces() {
      const data = await getProvinces();
      setProvinces(data);
    }
    loadProvinces();
  }, []);

  // Studios and single rooms legitimately have 0 bedrooms.
  const requiresBedrooms = form.propertyType !== 'studio' && form.propertyType !== 'room';

  const canSubmit = useMemo(() => {
    return (
      form.title.trim().length > 5 &&
      form.description.trim().length > 20 &&
      form.metro.trim().length > 0 &&
      form.monthlyRent > 0 &&
      (!requiresBedrooms || form.bedrooms > 0) &&
      form.bathrooms > 0
    );
  }, [form, requiresBedrooms]);

  const formProgress = useMemo(() => {
    // Only track required fields for progress
    const fields = {
      title: form.title.trim().length > 5,
      description: form.description.trim().length > 20,
      metro: form.metro.trim().length > 0,
      monthlyRent: form.monthlyRent > 0,
      bedrooms: !requiresBedrooms || form.bedrooms > 0,
      bathrooms: form.bathrooms > 0,
    };
    const completed = Object.values(fields).filter(Boolean).length;
    const total = Object.keys(fields).length;
    return Math.round((completed / total) * 100);
  }, [form]);

  const handleChange = (key: keyof FormState, value: string | number | boolean | UtilitiesType | PropertyType | StatusOption) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
    // Clear field error when user starts typing
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleProvinceChange = async (provinceCode: string) => {
    const selectedProvince = provinces.find((p) => p.code === provinceCode);
    if (selectedProvince) {
      setForm((prev) => ({
        ...prev,
        provinceCode,
        province: selectedProvince.name,
        metro: '',
        city: '',
        neighborhoods: [],
      }));
      setHasUnsavedChanges(true);

      const metros = await getMetrosForProvince(provinceCode);
      setAvailableMetros(metros);
      setAvailableNeighborhoods([]);
    }
  };

  const handleMetroChange = (metro: string) => {
    setForm((prev) => ({
      ...prev,
      metro,
      city: metro,
      neighborhoods: [],
    }));
    setHasUnsavedChanges(true);

    const neighborhoods = getNeighborhoodsForMetro(metro);
    setAvailableNeighborhoods(neighborhoods);
  };

  const toggleNeighborhood = (neighborhood: string) => {
    setForm((prev) => {
      const exists = prev.neighborhoods.includes(neighborhood);
      const next = exists
        ? prev.neighborhoods.filter((n) => n !== neighborhood)
        : [...prev.neighborhoods, neighborhood];
      return { ...prev, neighborhoods: next };
    });
    setHasUnsavedChanges(true);
  };

  const addAmenity = () => {
    const trimmed = amenityInput.trim();
    if (!trimmed) return;
    if (form.amenities.includes(trimmed)) {
      setAmenityInput('');
      return;
    }
    setForm((prev) => ({ ...prev, amenities: [...prev.amenities, trimmed] }));
    setAmenityInput('');
  };

  const removeAmenity = (item: string) => {
    setForm((prev) => ({ ...prev, amenities: prev.amenities.filter((a) => a !== item) }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !currentUser) return;

    setUploadingPhoto(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large (max 5MB)`);
        }

        // Create unique filename
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 9);
        const extension = file.name.split('.').pop();
        const filename = `properties/${currentUser.uid}/${timestamp}_${randomStr}.${extension}`;

        // Upload to Firebase Storage
        const storageRef = ref(storage, filename);
        await uploadBytes(storageRef, file);

        // Get download URL
        const downloadURL = await getDownloadURL(storageRef);
        return downloadURL;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      
      setForm((prev) => ({
        ...prev,
        photoUrls: [...prev.photoUrls, ...uploadedUrls],
        primaryPhotoIndex: prev.photoUrls.length === 0 ? 0 : prev.primaryPhotoIndex,
      }));
      setHasUnsavedChanges(true);
      
      // Clear the file input
      e.target.value = '';
    } catch (error: unknown) {
      console.error('Error uploading photos:', error);
      setError(getErrorMessage(error, 'Failed to upload photos. Please try again.'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addPhoto = () => {
    const trimmed = photoInput.trim();
    if (!trimmed) return;
    setForm((prev) => ({ 
      ...prev, 
      photoUrls: [...prev.photoUrls, trimmed], 
      primaryPhotoIndex: prev.photoUrls.length === 0 ? 0 : prev.primaryPhotoIndex 
    }));
    setPhotoInput('');
    setHasUnsavedChanges(true);
  };

  const removePhoto = (url: string) => {
    setForm((prev) => {
      const removedIndex = prev.photoUrls.indexOf(url);
      const nextPhotos = prev.photoUrls.filter((p) => p !== url);
      // Keep the same photo primary where possible instead of always resetting to the first.
      let primaryPhotoIndex = prev.primaryPhotoIndex;
      if (removedIndex === prev.primaryPhotoIndex) {
        primaryPhotoIndex = 0;
      } else if (removedIndex < prev.primaryPhotoIndex) {
        primaryPhotoIndex = prev.primaryPhotoIndex - 1;
      }
      if (primaryPhotoIndex >= nextPhotos.length) primaryPhotoIndex = 0;
      return { ...prev, photoUrls: nextPhotos, primaryPhotoIndex };
    });
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || userRole !== 'landlord' || !canSubmit) return;
    
    // Validate form
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (form.title.trim().length < 6) errors.title = 'Title must be at least 6 characters';
    if (form.description.trim().length < 20) errors.description = 'Description must be at least 20 characters';
    if (form.metro.trim().length < 1) errors.metro = 'Metro/City is required';
    if (form.monthlyRent <= 0) errors.monthlyRent = 'Monthly rent must be greater than 0';
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setSuccessId(null);

    try {
      const payload = {
        landlordId: currentUser.uid,
        title: form.title.trim(),
        description: form.description.trim(),
        propertyType: form.propertyType,
        address: {
          street: form.street.trim(),
          suburb: form.suburb.trim(),
          city: form.metro || form.city.trim(),
          province: form.province.trim(),
          postalCode: form.postalCode.trim(),
        },
        provinceCode: form.provinceCode,
        metro: form.metro,
        neighborhoods: form.neighborhoods,
        bedrooms: form.bedrooms,
        bathrooms: form.bathrooms,
        squareMeters: form.squareMeters,
        furnished: form.furnished,
        monthlyRent: form.monthlyRent,
        deposit: form.deposit,
        utilities: form.utilities,
        amenities: form.amenities,
        petFriendly: form.petFriendly,
        smokingAllowed: form.smokingAllowed,
        photoUrls: form.photoUrls,
        primaryPhotoIndex: form.photoUrls.length > 0 ? form.primaryPhotoIndex : 0,
        availableFrom: Timestamp.fromDate(new Date(form.availableFrom)),
        status: form.status,
        minLeaseDuration: form.minLeaseDuration,
        maxOccupants: form.maxOccupants,
        views: 0,
        inquiries: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: form.status !== 'draft',
      };

      const docRef = await addDoc(collection(firestore, 'properties'), payload);
      setSuccessId(docRef.id);
      setForm(defaultFormState);
      setHasUnsavedChanges(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      console.error('Error creating property', err);
      setError(getErrorMessage(err, 'Could not create property. Please try again.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 max-w-lg w-full text-center space-y-4">
          <h1 className="text-3xl font-extrabold text-text-primary">Landlord access only</h1>
          <p className="text-text-secondary">Sign in to create and manage listings.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/login')}>
              Sign in
            </button>
            <button className="btn-secondary w-full sm:w-auto" onClick={() => navigate('/signup?role=landlord')}>
              Create landlord account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (userRole && userRole !== 'landlord') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 max-w-lg w-full text-center space-y-4">
          <h1 className="text-3xl font-extrabold text-text-primary">Switch to landlord</h1>
          <p className="text-text-secondary">This flow is dedicated to landlord listings.</p>
          <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/login')}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-32 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-12 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <header className="bg-white/80 backdrop-blur-md border border-gray-100 shadow-lg rounded-2xl px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/landlord/dashboard')}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center shadow-lg hover:shadow-xl transition-all shrink-0 cursor-pointer"
                aria-label="Go to dashboard home"
              >
                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </button>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-text-muted font-semibold">Landlord</p>
                <h1 className="text-lg sm:text-xl font-extrabold text-text-primary truncate">Create property</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-left sm:text-right">
                <p className="text-xs text-text-muted font-semibold">Completion</p>
                <p className="text-sm font-bold text-primary-300">{formProgress}%</p>
              </div>
            </div>
          </div>
        </header>

        {/* Progress bar */}
        <div className="bg-gray-200 rounded-full h-2 overflow-hidden" role="progressbar" aria-valuenow={formProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Form completion progress">
          <div 
            className="bg-gradient-to-r from-primary-200 to-primary-300 h-full transition-all duration-300 ease-out"
            style={{ width: `${formProgress}%` }}
          ></div>
        </div>

        {successId && (
          <div className="border-l-4 border-green-500 bg-green-50 text-green-700 px-4 py-3 rounded-xl shadow-sm flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5" />
            <div>
              <p className="font-semibold">Listing created</p>
              <p className="text-sm">Property ID: {successId}. You can view it in “My Properties”.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="border-l-4 border-red-500 bg-red-50 text-red-700 px-4 py-3 rounded-xl shadow-sm">
            <p className="font-semibold">Could not save</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader
              icon={<Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />}
              title="Property basics"
              helper="Headline, description, and type"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="property-title" className="block text-sm font-semibold text-text-primary mb-1">
                  Title <span className="text-primary-300 ml-1" aria-label="required">*</span>
                </label>
                <input
                  id="property-title"
                  className={`input ${fieldErrors.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  placeholder="Sunny 2-bed in Cape Town CBD"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  aria-invalid={fieldErrors.title ? 'true' : 'false'}
                  aria-describedby={fieldErrors.title ? 'title-error' : undefined}
                />
                {fieldErrors.title && (
                  <p id="title-error" className="mt-1 text-sm text-red-600" role="alert">
                    {fieldErrors.title}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="property-type" className="block text-sm font-semibold text-text-primary mb-1">
                  Type
                </label>
                <select
                  id="property-type"
                  className="input"
                  value={form.propertyType}
                  onChange={(e) => handleChange('propertyType', e.target.value as PropertyType)}
                  aria-label="Property type"
                >
                  {(['apartment', 'house', 'townhouse', 'studio', 'room'] as PropertyType[]).map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="property-description" className="block text-sm font-semibold text-text-primary mb-1">
                Description <span className="text-primary-300 ml-1" aria-label="required">*</span>
              </label>
              <textarea
                id="property-description"
                className={`input min-h-[120px] ${fieldErrors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                placeholder="Highlight layout, light, amenities, and nearby hotspots."
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                aria-invalid={fieldErrors.description ? 'true' : 'false'}
                aria-describedby={fieldErrors.description ? 'description-error' : undefined}
              />
              {fieldErrors.description && (
                <p id="description-error" className="mt-1 text-sm text-red-600" role="alert">
                  {fieldErrors.description}
                </p>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <NumberField
                label="Bedrooms"
                value={form.bedrooms}
                onChange={(value) => handleChange('bedrooms', value)}
                min={0}
                id="bedrooms"
              />
              <NumberField
                label="Bathrooms"
                value={form.bathrooms}
                onChange={(value) => handleChange('bathrooms', value)}
                min={0}
                id="bathrooms"
              />
              <NumberField
                label="Size (m²)"
                value={form.squareMeters}
                onChange={(value) => handleChange('squareMeters', value)}
                min={0}
                id="square-meters"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <ToggleField
                label="Furnished"
                value={form.furnished}
                onChange={(value) => handleChange('furnished', value)}
              />
              <ToggleField
                label="Pet friendly"
                value={form.petFriendly}
                onChange={(value) => handleChange('petFriendly', value)}
              />
              <ToggleField
                label="Smoking allowed"
                value={form.smokingAllowed}
                onChange={(value) => handleChange('smokingAllowed', value)}
              />
            </div>
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader
              icon={<MapPin className="w-4 h-4 sm:w-5 sm:h-5" />}
              title="Location"
              helper="Accurate address helps tenants find you faster"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Street" value={form.street} onChange={(v) => handleChange('street', v)} placeholder="123 Bree Street" id="street" />
              <Field label="Suburb" value={form.suburb} onChange={(v) => handleChange('suburb', v)} placeholder="Gardens" id="suburb" />
              
              <div>
                <label htmlFor="province" className="block text-sm font-semibold text-text-primary mb-1">
                  Province <span className="text-primary-300 ml-1" aria-label="required">*</span>
                </label>
                <select
                  id="province"
                  className="input"
                  value={form.provinceCode}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  required
                  aria-label="Province"
                >
                  <option value="">Select province</option>
                  {provinces.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="metro" className="block text-sm font-semibold text-text-primary mb-1">
                  Metro / City <span className="text-primary-300 ml-1" aria-label="required">*</span>
                </label>
                <select
                  id="metro"
                  className="input"
                  value={form.metro}
                  onChange={(e) => handleMetroChange(e.target.value)}
                  disabled={!form.provinceCode}
                  required
                  aria-label="Metro or city"
                >
                  <option value="">Select metro/city</option>
                  {availableMetros.map((metro) => (
                    <option key={metro} value={metro}>
                      {metro}
                    </option>
                  ))}
                </select>
              </div>

              <Field label="Postal code" value={form.postalCode} onChange={(v) => handleChange('postalCode', v)} placeholder="8001" id="postal-code" />
            </div>

            {availableNeighborhoods.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Neighborhoods (optional)
                </label>
                <p className="text-xs text-text-secondary mb-3">
                  Select specific neighborhoods to help tenants find your property
                </p>
                <div className="flex gap-2 flex-wrap">
                  {availableNeighborhoods.map((neighborhood) => (
                    <button
                      key={neighborhood}
                      type="button"
                      onClick={() => toggleNeighborhood(neighborhood)}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-all ${
                        form.neighborhoods.includes(neighborhood)
                          ? 'bg-primary-200 text-white border-primary-300'
                          : 'bg-white text-text-secondary border-gray-200 hover:border-primary-100'
                      }`}
                    >
                      {neighborhood}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader
              icon={<Building2 className="w-4 h-4 sm:w-5 sm:h-5" />}
              title="Pricing & terms"
              helper="Rent, deposit, utilities, availability"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <NumberField
                label="Monthly rent (R)"
                value={form.monthlyRent}
                onChange={(value) => handleChange('monthlyRent', value)}
                min={0}
                error={fieldErrors.monthlyRent}
                id="monthly-rent"
              />
              <NumberField 
                label="Deposit (R)" 
                value={form.deposit} 
                onChange={(value) => handleChange('deposit', value)} 
                min={0}
                id="deposit"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="utilities" className="block text-sm font-semibold text-text-primary mb-1">
                  Utilities
                </label>
                <select
                  id="utilities"
                  className="input"
                  value={form.utilities}
                  onChange={(e) => handleChange('utilities', e.target.value as UtilitiesType)}
                  aria-label="Utilities inclusion"
                >
                  {(['included', 'excluded', 'partially-included'] as UtilitiesType[]).map((option) => (
                    <option key={option} value={option}>
                      {option.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="available-from" className="block text-sm font-semibold text-text-primary mb-1">
                  Available from
                </label>
                <input
                  id="available-from"
                  type="date"
                  className="input"
                  value={form.availableFrom}
                  onChange={(e) => handleChange('availableFrom', e.target.value)}
                  aria-label="Available from date"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <NumberField
                label="Minimum lease (months)"
                value={form.minLeaseDuration}
                onChange={(value) => handleChange('minLeaseDuration', value)}
                min={1}
                id="min-lease"
              />
              <NumberField
                label="Max occupants"
                value={form.maxOccupants}
                onChange={(value) => handleChange('maxOccupants', value)}
                min={1}
                id="max-occupants"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-1">
                Status
              </label>
              <div className="grid sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Property status">
                {(['available', 'pending', 'rented', 'draft'] as StatusOption[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleChange('status', status)}
                    role="radio"
                    aria-checked={form.status === status}
                    className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-all duration-150 ${
                      form.status === status
                        ? 'border-primary-200 bg-primary-50 text-primary-300 shadow-sm'
                        : 'border-gray-200 bg-white text-text-secondary hover:border-primary-100'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader icon={<Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />} title="Amenities" helper="What makes this place stand out?" />
            <div className="flex flex-wrap gap-2" role="list" aria-label="Selected amenities">
              {form.amenities.map((item) => (
                <span
                  key={item}
                  role="listitem"
                  className="px-3 py-1 rounded-full text-sm font-semibold bg-primary-50 text-primary-300 border border-primary-100 flex items-center gap-2"
                >
                  {item}
                  <button 
                    type="button" 
                    onClick={() => removeAmenity(item)} 
                    className="text-primary-300 hover:text-primary-400 w-5 h-5 flex items-center justify-center"
                    aria-label={`Remove ${item}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="amenity-input"
                className="input flex-1"
                placeholder="Add an amenity (e.g., Inverter, Biometric access)"
                value={amenityInput}
                onChange={(e) => setAmenityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAmenity();
                  }
                }}
                aria-label="New amenity"
              />
              <button 
                type="button" 
                className="btn-outline px-4 py-3 rounded-xl" 
                onClick={addAmenity}
                aria-label="Add amenity"
              >
                Add
              </button>
            </div>
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader icon={<Image className="w-4 h-4 sm:w-5 sm:h-5" />} title="Media" helper="Upload photos or add URLs for your listing" />
            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Upload Photos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary-300 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`cursor-pointer inline-flex flex-col items-center ${
                      uploadingPhoto ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    <Image className="w-12 h-12 text-gray-400 mb-3" />
                    <span className="text-sm font-semibold text-text-primary mb-1">
                      {uploadingPhoto ? 'Uploading...' : 'Click to upload photos'}
                    </span>
                    <span className="text-xs text-text-secondary">
                      PNG, JPG, WEBP up to 5MB each
                    </span>
                  </label>
                  {uploadingPhoto && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-primary-300 h-full transition-all duration-300 animate-pulse"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Photo Grid */}
              {form.photoUrls.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-text-primary">
                      Photos ({form.photoUrls.length})
                    </label>
                    <span className="text-xs text-text-secondary">
                      First photo will be the main image
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {form.photoUrls.map((url, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-gray-200">
                        <img src={url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                        {idx === 0 && (
                          <div className="absolute top-2 left-2 bg-primary-300 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-lg">
                            Main
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePhoto(url)}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          aria-label={`Remove photo ${idx + 1}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Or add URL */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Or Add Photo URL
                </label>
                <div className="flex gap-2">
                  <input
                    className="input flex-1"
                    placeholder="https://example.com/photo.jpg"
                    value={photoInput}
                    onChange={(e) => setPhotoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPhoto();
                      }
                    }}
                  />
                  <button type="button" onClick={addPhoto} className="btn-secondary px-4 whitespace-nowrap">
                    Add URL
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <SectionHeader icon={<Home className="w-4 h-4 sm:w-5 sm:h-5" />} title="Finalize" helper="Review details before publishing" />
            <div className="grid md:grid-cols-2 gap-4 text-sm text-text-secondary">
              <div className="p-4 rounded-xl bg-primary-50 border border-primary-100">
                <p className="font-semibold text-text-primary mb-2">Quick recap</p>
                <p>{form.bedrooms} bed / {form.bathrooms} bath, {form.squareMeters} m²</p>
                <p className="mt-1">R{form.monthlyRent.toLocaleString()} / month • Deposit R{form.deposit.toLocaleString()}</p>
                <p className="mt-1 capitalize">Status: {form.status}</p>
              </div>
              <div className="p-4 rounded-xl bg-accent-purple/10 border border-accent-purple/30">
                <p className="font-semibold text-text-primary mb-2">Publishing notes</p>
                <p>Listings marked “draft” stay hidden until you set them to available.</p>
                <p className="mt-1">You can edit everything later from “My Properties”.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary px-6 py-3 rounded-xl"
                onClick={() => {
                  if (hasUnsavedChanges && !window.confirm('Discard your changes? Your progress will be lost.')) {
                    return;
                  }
                  navigate('/landlord/properties');
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary px-6 py-3 rounded-xl flex items-center justify-center gap-2"
                disabled={!canSubmit || submitting}
              >
                {submitting ? 'Saving...' : 'Save property'}
              </button>
            </div>
          </div>
        </form>

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  helper: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, helper }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-[0.2em] text-text-muted font-semibold">Section</p>
      <h3 className="text-lg sm:text-xl font-bold text-text-primary">{title}</h3>
      <p className="text-text-secondary text-xs sm:text-sm">{helper}</p>
    </div>
    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center text-white shadow-lg shrink-0">
      {icon}
    </div>
  </div>
);

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  id?: string;
}

const Field: React.FC<FieldProps> = ({ label, value, onChange, placeholder, required, error, id }) => {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '-');
  
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-semibold text-text-primary mb-1">
        {label}
        {required ? <span className="text-primary-300 ml-1" aria-label="required">*</span> : null}
      </label>
      <input
        id={inputId}
        className={`input ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : undefined}
      />
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  error?: string;
  id?: string;
}

const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange, min, error, id }) => {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '-');
  
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-semibold text-text-primary mb-1">
        {label}
      </label>
      <input
        id={inputId}
        type="number"
        className={`input ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
        value={value}
        min={min}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : undefined}
      />
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

interface ToggleFieldProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  id?: string;
}

const ToggleField: React.FC<ToggleFieldProps> = ({ label, value, onChange, id }) => {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, '-');
  
  return (
    <div>
      <label id={`${fieldId}-label`} className="block text-sm font-semibold text-text-primary mb-1">
        {label}
      </label>
      <div 
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-gray-200 bg-white"
        role="radiogroup"
        aria-labelledby={`${fieldId}-label`}
      >
        <button
          type="button"
          onClick={() => onChange(true)}
          role="radio"
          aria-checked={value === true}
          className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all duration-150 ${
            value ? 'bg-primary-200 text-white shadow-sm' : 'text-text-secondary'
          }`}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          role="radio"
          aria-checked={value === false}
          className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all duration-150 ${
            !value ? 'bg-gray-100 text-text-primary' : 'text-text-secondary'
          }`}
        >
          No
        </button>
      </div>
    </div>
  );
};

export default CreateProperty;
